import type { OverviewResponse } from "contracts/api/dashboard/responses";
import type { Issue } from "contracts/api/issue/responses";
import type {
  MapChangeType,
  MapConfidence,
  MapTargetLane,
} from "../../enums/map";
import {
  MAP_CHANGE_STATUS,
  MAP_CHANGE_TYPE,
  MAP_CONFIDENCE,
  MAP_EVIDENCE_KIND,
  MAP_EVIDENCE_TOPIC,
  MAP_TARGET_LANE,
} from "../../enums/map";
import { pageDisplayLabel } from "../pages/boundaries/page-label.parser";
import { mapSlug } from "./boundaries/map-slug.formatter";
import { relatedPageIds } from "./mapDerivation";
import { coverageEvidence, issueEvidence } from "./mapEvidence";
import type {
  MapChange,
  MapCurrentGroup,
  MapPageFact,
  MapVocabulary,
} from "./mapTypes";
import { mapPatternName } from "./mapVocabulary";

export type ChangeInput = {
  data: OverviewResponse;
  group: MapCurrentGroup;
  facts: MapPageFact[];
  issues: Issue[];
  vocabulary: MapVocabulary;
};

export function createHubChange(input: ChangeInput): MapChange {
  const count = input.facts.length;
  return buildChange(input, {
    type: MAP_CHANGE_TYPE.CREATE_HUB,
    title: `Create a ${input.group.label} hub`,
    summary: `${count} sibling pages need one clear organising point.`,
    currentState: `${count} related pages sit beneath /${input.group.key}/, but no section-level organising page was found in the assessed inventory.`,
    proposedState: `Create one ${input.group.label} hub that introduces the area and routes to its represented content and recorded destinations.`,
    targetRole: "Section hub or organising page",
    targetLane: MAP_TARGET_LANE.ORGANISE,
    confidence:
      input.issues.length > 0 ? MAP_CONFIDENCE.STRONG : MAP_CONFIDENCE.SOME,
    evidence: [
      {
        kind: MAP_EVIDENCE_KIND.STRUCTURE,
        topic: MAP_EVIDENCE_TOPIC.SIBLING_URL_STRUCTURE,
        detail: `${count} assessed-inventory paths share /${input.group.key}/.`,
      },
      {
        kind: MAP_EVIDENCE_KIND.STRUCTURE,
        topic: MAP_EVIDENCE_TOPIC.NO_ORGANISING_PAGE,
        detail: `No section-level /${input.group.key}/ page is present in this Scan inventory.`,
      },
      ...issueEvidence(input.issues, pageIds(input.facts)).slice(0, 1),
      coverageEvidence(input.facts),
    ],
    uncertainties: [
      "The final navigation label and target URL require client input.",
      "Existing off-scan content may affect this recommendation.",
    ],
  });
}

export function consolidateChange(
  input: ChangeInput,
  overlapReason: string,
): MapChange {
  const count = input.facts.length;
  return buildChange(input, {
    type: MAP_CHANGE_TYPE.CONSOLIDATE_PAGES,
    title: `Consolidate overlapping ${input.group.label} pages`,
    summary: `${count} pages appear to serve materially overlapping purposes.`,
    currentState: `${count} pages in ${input.group.label} have strongly aligned titles and URL structure.`,
    proposedState: `Review the overlapping content and agree one combined destination before retiring or redirecting anything.`,
    targetRole: "Combined page or content pattern",
    targetLane: MAP_TARGET_LANE.PATTERN,
    confidence: MAP_CONFIDENCE.STRONG,
    evidence: [
      {
        kind: MAP_EVIDENCE_KIND.STRUCTURE,
        topic: MAP_EVIDENCE_TOPIC.MATERIAL_OVERLAP,
        detail: overlapReason,
      },
      ...issueEvidence(input.issues, pageIds(input.facts)).slice(0, 1),
      coverageEvidence(input.facts),
    ],
    uncertainties: [
      "Content overlap must be reviewed before pages are combined.",
      "The canonical destination and redirect approach are not approved.",
    ],
  });
}

export function improvePatternChange(input: ChangeInput): MapChange {
  const role = input.facts[0]?.roleLabel.toLowerCase() ?? "content pattern";
  const patternName = mapPatternName(
    input.vocabulary,
    input.facts.map((fact) => fact.page),
  );
  const title =
    input.facts.length === 1 && input.facts[0]
      ? `Improve ${pageDisplayLabel(input.facts[0].page, input.data.site)}`
      : `Improve the ${input.group.label} ${patternName}`;
  return buildChange(input, {
    type: MAP_CHANGE_TYPE.IMPROVE_PATTERN,
    title,
    summary:
      input.facts.length === 1
        ? "This page has a supported structural problem with a realistic in-place fix."
        : `${input.facts.length} related pages share a supported structural problem.`,
    currentState: `${input.facts.length} ${input.group.label} page${input.facts.length === 1 ? " uses" : "s use"} a similar ${role}, with relevant orientation or path findings.`,
    proposedState: `Improve the shared pattern so these pages provide clearer orientation, structure and onward steps without changing their intended role.`,
    targetRole: patternName,
    targetLane: MAP_TARGET_LANE.PATTERN,
    confidence:
      input.issues.length > 1 ? MAP_CONFIDENCE.STRONG : MAP_CONFIDENCE.SOME,
    evidence: [
      {
        kind: "structure" as const,
        topic: MAP_EVIDENCE_TOPIC.SHARED_PAGE_ROLE,
        detail: `${input.facts.length} page${input.facts.length === 1 ? "" : "s"} ${input.facts.length === 1 ? "shares" : "share"} the same current group and inferred structural role.`,
      },
      ...issueEvidence(input.issues, pageIds(input.facts)).slice(0, 2),
      coverageEvidence(input.facts),
    ].slice(0, 4),
    uncertainties: [
      "The final pattern requirements need client and delivery-team input.",
      "Page ownership is not available in the current Scan data.",
    ],
  });
}

export function reorganiseGroupChange(input: ChangeInput): MapChange {
  const roles = new Set(input.facts.map((fact) => fact.roleLabel)).size;
  return buildChange(input, {
    type: MAP_CHANGE_TYPE.REORGANISE_GROUP,
    title: `Reorganise ${input.group.label} content`,
    summary: `${input.facts.length} pages span ${roles} structural roles without a clear shared organisation.`,
    currentState: `${input.facts.length} pages currently sit in ${input.group.label} across ${roles} inferred roles, with relevant orientation evidence.`,
    proposedState: `Organise the group around clearer page roles and relationships while preserving useful current content.`,
    targetRole: input.vocabulary.organise,
    targetLane: MAP_TARGET_LANE.ORGANISE,
    confidence: MAP_CONFIDENCE.SOME,
    evidence: [
      {
        kind: "structure" as const,
        topic: MAP_EVIDENCE_TOPIC.MIXED_STRUCTURAL_ROLES,
        detail: `${roles} distinct page roles are represented within one current group.`,
      },
      ...issueEvidence(input.issues, pageIds(input.facts)).slice(0, 2),
      coverageEvidence(input.facts),
    ].slice(0, 4),
    uncertainties: [
      "The final grouping and navigation labels require client approval.",
      "Audience priorities are not confirmed in the current Scan data.",
    ],
  });
}

export function unresolvedChange(input: ChangeInput): MapChange {
  return buildChange(input, {
    type: MAP_CHANGE_TYPE.UNRESOLVED,
    title: `Resolve the role of ${input.group.label}`,
    summary:
      "The current evidence does not support a confident target structure.",
    currentState: `${input.facts.length} pages are grouped here, but their intended role or relationships cannot be established confidently.`,
    proposedState:
      "Confirm the strategic purpose, audience and intended relationships before agreeing a structural change.",
    targetRole: "Role to confirm",
    targetLane: MAP_TARGET_LANE.ORGANISE,
    confidence: MAP_CONFIDENCE.LIMITED,
    evidence: [
      {
        kind: MAP_EVIDENCE_KIND.INFERENCE,
        topic: MAP_EVIDENCE_TOPIC.CLIENT_CONTEXT_REQUIRED,
        detail:
          "Available structure and assessment evidence is incomplete or conflicting.",
      },
      coverageEvidence(input.facts),
    ],
    uncertainties: [
      "Strategic intent and audience context are required.",
      "The correct destination cannot be inferred from current evidence.",
    ],
  });
}

export function buildChange(
  input: ChangeInput,
  details: {
    type: MapChangeType;
    title: string;
    summary: string;
    currentState: string;
    proposedState: string;
    targetRole: string;
    targetLane: MapTargetLane;
    confidence: MapConfidence;
    evidence: MapChange["evidence"];
    uncertainties: string[];
  },
): MapChange {
  return {
    id: `map-${mapSlug(`${details.type}-${input.group.key}-${details.title}`)}`,
    outcomeKey: `${input.group.key}:${details.type}`,
    type: details.type,
    status:
      details.type === MAP_CHANGE_TYPE.UNRESOLVED
        ? MAP_CHANGE_STATUS.UNRESOLVED
        : MAP_CHANGE_STATUS.PENDING,
    title: details.title,
    summary: details.summary,
    currentState: details.currentState,
    proposedState: details.proposedState,
    currentGroupKey: input.group.key,
    currentGroupLabel: input.group.label,
    targetGroup: input.group.label,
    targetRole: details.targetRole,
    targetLane: details.targetLane,
    pages: input.facts.map((fact) => fact.page),
    pageFacts: input.facts,
    issues: input.issues,
    evidence: details.evidence,
    uncertainties: details.uncertainties,
    confidence: details.confidence,
    relatedPageIds: relatedPageIds(
      input.data,
      input.facts.map((fact) => fact.page),
    ),
    clientNote: "",
    adjusted: false,
  };
}

function pageIds(facts: MapPageFact[]): Set<string> {
  return new Set(facts.map((fact) => fact.page.id));
}
